import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// Drives the Reorder Suggestions screen.
//
// Returns:
//   items                — all active items with inventory state
//   needingReorder       — subset where current_on_hand < par_qty
//   groupedBySupplier    — needingReorder grouped by preferred supplier
//   restockCost          — total $ to bring everything back to par
//   recentOrders         — last 20 purchase_orders with line items
//   loading, error
//   refresh
//   sendOrder            — creates a PO + line items + updates on-hand
//
// On-hand is treated as a snapshot — manager updates during the
// "Send Order" flow when they're physically counting stock anyway.
// Future: dedicated stocktake screen, automated inference from
// (cumulative receivings - cumulative usage).
export function useReorderData() {
  const [items, setItems] = useState([])
  const [recentOrders, setRecentOrders] = useState([])
  // The current restaurant — used to populate the email body's
  // "Order for [Restaurant Name]" line.
  const [restaurant, setRestaurant] = useState(null)
  // 30-day activity windows for the AI Suggest Reorder feature.
  // Loaded alongside items so the modal can fire its API call without
  // another round-trip.
  const [receiveLogs, setReceiveLogs] = useState([])
  const [wasteLogs, setWasteLogs]     = useState([])
  const [itemSales, setItemSales]     = useState([])
  // The current unit's pending approve-and-execute reorder action (frozen by
  // the morning brief). null when there's nothing awaiting approval.
  const [pendingReorderAction, setPendingReorderAction] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Multi-unit scope — null for single-unit (queries byte-identical).
  const { currentRestaurantId } = useCurrentUnit()

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Apply the unit filter only when grouped; single-unit (null) is
    // untouched and keeps relying on RLS membership scoping.
    const scope = (q) => (currentRestaurantId ? q.eq('restaurant_id', currentRestaurantId) : q)
    try {
      // 30-day window for the AI Suggest analysis. Build it on the
      // client so we can drop it into the suggest-reorder Edge Function
      // without re-querying.
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      const sinceTs = thirtyDaysAgo.toISOString()
      const sinceDate = thirtyDaysAgo.toLocaleDateString('en-CA')

      // The restaurant lookup powers the "Order for [Restaurant Name]"
      // email line. For a grouped user we must resolve the SELECTED
      // unit, not the first-active heuristic; for single-unit the
      // original first-active lookup is preserved byte-for-byte.
      const restQuery = currentRestaurantId
        ? supabase
            .from('restaurants')
            .select('id, name, timezone')
            .eq('id', currentRestaurantId)
            .single()
        : supabase
            .from('restaurants')
            .select('id, name, timezone')
            .eq('active', true)
            .order('created_at')
            .limit(1)
            .single()

      const [itemsRes, ordersRes, restRes, recvRes, wasteRes, salesRes, pendingRes] = await Promise.all([
        // Nested select on suppliers gives us preferred_supplier
        // (id, name, contact) without a separate query.
        scope(supabase
          .from('items')
          .select(`
            id, name, category, unit, cost_per_unit,
            par_qty, current_on_hand, last_stocktake_at, lead_time_days,
            pack_size, pack_unit,
            preferred_supplier:suppliers(id, name, contact)
          `)
          .eq('active', true)
          .order('name')),
        // cost_per_unit_at_log included so the email body can show
        // "@ $9.50/lb" per line.
        scope(supabase
          .from('purchase_orders')
          .select(`
            id, supplier_id, supplier_name, status, notes,
            total_cost, created_at, sent_at, received_at,
            items:purchase_order_items(id, item_name, quantity, unit, cost_per_unit_at_log, total_cost)
          `)
          .order('created_at', { ascending: false })
          .limit(20)),
        restQuery,
        scope(supabase
          .from('receive_logs')
          .select('item_id, quantity, unit, logged_at')
          .gte('logged_at', sinceTs)),
        scope(supabase
          .from('waste_logs')
          .select('item_id, quantity, unit, logged_at')
          .gte('logged_at', sinceTs)),
        scope(supabase
          .from('item_sales')
          .select('item_id, quantity_sold, period_end')
          .gte('period_end', sinceDate)),
        // Pending approve-and-execute reorder action for this unit (latest).
        // RLS already scopes to the caller's units; the unit filter narrows
        // to the SELECTED unit for grouped operators.
        scope(supabase
          .from('pending_reorder_actions')
          .select('id, restaurant_id, brief_date, spec, dollar_impact, status, created_at')
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(1)),
      ])
      if (itemsRes.error)  throw itemsRes.error
      if (ordersRes.error) throw ordersRes.error
      // restRes / recvRes / wasteRes / salesRes / pendingRes can fail silently
      // — those just weaken the AI input quality / hide the approve card, not
      // the reorder list itself.

      setItems(itemsRes.data ?? [])
      setRecentOrders(ordersRes.data ?? [])
      if (restRes.data) setRestaurant(restRes.data)
      setReceiveLogs(recvRes.data ?? [])
      setWasteLogs(wasteRes.data ?? [])
      setItemSales(salesRes.data ?? [])
      setPendingReorderAction(pendingRes.data?.[0] ?? null)
    } catch (e) {
      setError(e)
      // eslint-disable-next-line no-console
      console.error('[useReorderData]', e)
    } finally {
      setLoading(false)
    }
  }, [currentRestaurantId])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Same midnight rollover + visibility refresh pattern as the
  // other data hooks. Pars don't change daily but cleaner UX to
  // re-fetch fresh data on focus regardless.
  useEffect(() => {
    let lastDate = new Date().toDateString()
    const id = setInterval(() => {
      const today = new Date().toDateString()
      if (today !== lastDate) {
        lastDate = today
        fetchAll()
      }
    }, 60_000)
    return () => clearInterval(id)
  }, [fetchAll])

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') {
        fetchAll()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchAll])

  const needingReorder = useMemo(
    () => items.filter(
      (i) => Number(i.par_qty) > 0 && Number(i.current_on_hand) < Number(i.par_qty)
    ),
    [items]
  )

  const groupedBySupplier = useMemo(() => {
    const groups = new Map()
    for (const i of needingReorder) {
      const supplierId   = i.preferred_supplier?.id ?? '__unassigned'
      const supplierName = i.preferred_supplier?.name ?? 'Unassigned'
      const supplier     = i.preferred_supplier ?? null
      const prev = groups.get(supplierId) || {
        supplierId, supplierName, supplier, items: []
      }
      prev.items.push(i)
      groups.set(supplierId, prev)
    }
    // Sort groups by supplier name; Unassigned last.
    return [...groups.values()].sort((a, b) => {
      if (a.supplierId === '__unassigned') return 1
      if (b.supplierId === '__unassigned') return -1
      return a.supplierName.localeCompare(b.supplierName)
    })
  }, [needingReorder])

  const restockCost = useMemo(() => {
    return needingReorder.reduce((sum, i) => {
      const need = Math.max(0, Number(i.par_qty) - Number(i.current_on_hand))
      return sum + need * Number(i.cost_per_unit)
    }, 0)
  }, [needingReorder])

  // Send a supplier order. Atomically (per the manager's mental model):
  //   1. Insert purchase_orders header with running total
  //   2. Insert purchase_order_items rows
  //   3. Update items.current_on_hand for any line where the manager
  //      provided a fresh stocktake count
  //
  // If step 2 or 3 fails after step 1, the PO record exists with
  // partial data. Acceptable for the MVP — we can add an RPC for
  // atomic transactions later if needed.
  const sendOrder = useCallback(async ({ supplier, lineItems, notes }) => {
    if (!lineItems || lineItems.length === 0) {
      throw new Error('No line items to send.')
    }

    const total = lineItems.reduce((s, li) => s + Number(li.total_cost || 0), 0)

    // 1. Header. For single-unit (null) we omit restaurant_id and let
    // the 0019 default trigger fill it — byte-identical to before. For
    // a grouped user we set it explicitly so the PO files under the
    // SELECTED unit rather than the trigger's first-membership default.
    const { data: po, error: poErr } = await supabase
      .from('purchase_orders')
      .insert({
        supplier_id: supplier?.id ?? null,
        supplier_name: supplier?.name ?? 'Unassigned',
        status: 'sent',
        notes: notes?.trim() ? notes.trim() : null,
        total_cost: Number(total.toFixed(2)),
        sent_at: new Date().toISOString(),
        ...(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
      })
      .select()
      .single()
    if (poErr) throw poErr

    // 2. Line items
    const liInserts = lineItems.map((li) => ({
      po_id: po.id,
      item_id: li.item_id,
      item_name: li.item_name,
      quantity: Number(li.quantity),
      unit: li.unit,
      cost_per_unit_at_log: Number(li.cost_per_unit),
      total_cost: Number(Number(li.total_cost).toFixed(2)),
    }))
    const { error: liErr } = await supabase
      .from('purchase_order_items')
      .insert(liInserts)
    if (liErr) throw liErr

    // 3. Stocktake updates (only for items where the manager edited
    //    on-hand during the order flow — no-op otherwise).
    const now = new Date().toISOString()
    for (const li of lineItems) {
      if (li.new_on_hand != null && li.item_id) {
        await supabase
          .from('items')
          .update({
            current_on_hand: Number(li.new_on_hand),
            last_stocktake_at: now,
          })
          .eq('id', li.item_id)
      }
    }

    await fetchAll()
    return po
  }, [fetchAll, currentRestaurantId])

  // Mark a sent PO as received. Calls the SQL function which atomically
  // inserts receive_logs rows (firing the on-hand trigger) and flips
  // the PO status. On success, refresh so the screen updates.
  const markReceived = useCallback(async (orderId) => {
    const { error: rpcErr } = await supabase.rpc(
      'mark_purchase_order_received',
      { p_po_id: orderId }
    )
    if (rpcErr) throw rpcErr
    await fetchAll()
  }, [fetchAll])

  // Approve-and-execute: place the PO for the pending action via the audited
  // execute-reorder edge fn (notify -> APPROVE -> execute). The fn is
  // idempotent (double-tap / retry places EXACTLY ONE PO). On a non-2xx
  // response or an edge error we throw so the screen can surface a friendly
  // retry; on success we refresh so the approve card clears and the new PO
  // shows in Recent orders.
  const approveReorder = useCallback(async (pendingActionId) => {
    const { data, error: fnErr } = await supabase.functions.invoke('execute-reorder', {
      body: {
        pending_action_id: pendingActionId,
        ...(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
      },
    })
    if (fnErr) throw fnErr
    if (data && data.ok === false) throw new Error(data.message || 'execute_failed')
    await fetchAll()
    return data
  }, [fetchAll, currentRestaurantId])

  // Skip routes through the SAME audited fn (mode=skip) so the dismissal is
  // recorded. Refresh clears the card.
  const skipReorder = useCallback(async (pendingActionId) => {
    const { data, error: fnErr } = await supabase.functions.invoke('execute-reorder', {
      body: {
        pending_action_id: pendingActionId,
        mode: 'skip',
        ...(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
      },
    })
    if (fnErr) throw fnErr
    await fetchAll()
    return data
  }, [fetchAll, currentRestaurantId])

  return {
    items,
    needingReorder,
    groupedBySupplier,
    restockCost,
    recentOrders,
    restaurant,
    receiveLogs,
    wasteLogs,
    itemSales,
    pendingReorderAction,
    loading,
    error,
    refresh: fetchAll,
    sendOrder,
    markReceived,
    approveReorder,
    skipReorder,
  }
}
